import { useEffect, useRef, useState } from 'react';
import { getStoredToken, refreshOAuthTokens } from '../../../services/tokenService';
import { toCamelCase } from '../../grid/utils';
import { isTerminalStatus, type AiImplementationRun } from '../types';

import { AI_IMPLEMENTATION_API_PATH } from '../api/aiImplementationApi';

export type ProgressStreamState = 'idle' | 'connecting' | 'connected' | 'reconnecting';

type Options = {
  enabled: boolean;
  runId: string | null;
  onProgress: (run: AiImplementationRun) => void;
  onTerminal: (run: AiImplementationRun) => void;
};

const delay = (milliseconds: number, signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timeout);
        resolve();
      },
      { once: true }
    );
  });

export const parseProgressEvents = (value: string): AiImplementationRun[] =>
  value
    .replace(/\r\n/g, '\n')
    .split('\n\n')
    .map((block) =>
      block
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n')
    )
    .filter(Boolean)
    .flatMap((data) => {
      try {
        return [toCamelCase(JSON.parse(data)) as AiImplementationRun];
      } catch {
        return [];
      }
    });

export const useAiImplementationProgressStream = ({
  enabled,
  runId,
  onProgress,
  onTerminal,
}: Options) => {
  const [state, setState] = useState<ProgressStreamState>('idle');
  const [error, setError] = useState<string | null>(null);
  const progressRef = useRef(onProgress);
  const terminalRef = useRef(onTerminal);

  progressRef.current = onProgress;
  terminalRef.current = onTerminal;

  useEffect(() => {
    if (!enabled || !runId) {
      setState('idle');
      setError(null);
      return;
    }

    const controller = new AbortController();
    let attempt = 0;

    const connect = async () => {
      while (!controller.signal.aborted) {
        setState(attempt === 0 ? 'connecting' : 'reconnecting');
        try {
          let token = getStoredToken();
          let response = await fetchProgress(runId, token, controller.signal);
          if (response.status === 401) {
            token = (await refreshOAuthTokens()).access_token;
            response = await fetchProgress(runId, token, controller.signal);
          }
          if (!response.ok || !response.body)
            throw new Error(`Progress stream failed with status ${response.status}.`);

          setState('connected');
          setError(null);
          attempt = 0;

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let terminal = false;

          while (!controller.signal.aborted) {
            const { value, done } = await reader.read();
            buffer += decoder.decode(value, { stream: !done }).replace(/\r\n/g, '\n');
            const boundary = buffer.lastIndexOf('\n\n');
            if (boundary >= 0) {
              const complete = buffer.slice(0, boundary + 2);
              buffer = buffer.slice(boundary + 2);
              for (const run of parseProgressEvents(complete)) {
                progressRef.current(run);
                if (isTerminalStatus(run.status)) {
                  terminal = true;
                  terminalRef.current(run);
                  break;
                }
              }
            }
            if (done || terminal) break;
          }

          reader.releaseLock();
          if (terminal || controller.signal.aborted) return;
          throw new Error('Progress stream was disconnected.');
        } catch (streamError) {
          if (controller.signal.aborted) return;
          setError(
            streamError instanceof Error ? streamError.message : 'Progress stream was disconnected.'
          );
          attempt += 1;
          await delay(Math.min(1000 * 2 ** (attempt - 1), 15000), controller.signal);
        }
      }
    };

    void connect();
    return () => controller.abort();
  }, [enabled, runId]);

  return { state, error };
};

const fetchProgress = (runId: string, token: string | null, signal: AbortSignal) => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  return fetch(
    `${baseUrl}${AI_IMPLEMENTATION_API_PATH}/ProgressStream?runId=${encodeURIComponent(runId)}`,
    {
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      signal,
    }
  );
};
